import { ShieldCheck, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import type {
  WorkflowComplianceSummary,
  WorkflowFrequencyCap,
  WorkflowQuietHours,
} from "@/hooks/use-workflows";

function getComplianceStatusStyles(status: NonNullable<WorkflowComplianceSummary["status"]>) {
  return {
    green: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
    yellow: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
    red: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
  }[status];
}

function formatQuietHours(quietHours: WorkflowQuietHours | null | undefined) {
  if (!quietHours?.enabled) {
    return "Not configured";
  }

  const timezoneLabel =
    quietHours.timezoneSource === "contact"
      ? `contact timezone fallback ${quietHours.fallbackTimezone}`
      : quietHours.fallbackTimezone;

  return `${quietHours.start}-${quietHours.end} · ${timezoneLabel}`;
}

function formatFrequencyCap(frequencyCap: WorkflowFrequencyCap | null | undefined) {
  if (!frequencyCap) {
    return "Not configured";
  }

  return `${frequencyCap.maxMessages} msg / ${frequencyCap.periodHours}h · ${frequencyCap.appliesTo}`;
}

export function WorkflowComplianceBadge({
  compliance,
}: {
  compliance?: WorkflowComplianceSummary | null;
}) {
  const status = compliance?.status;
  const warningCount = compliance?.checks.filter((check) => check.state === "warn").length ?? 0;

  if (!status) {
    return null;
  }

  const label =
    status === "green"
      ? "Compliance ready"
      : `${warningCount} compliance ${warningCount === 1 ? "warning" : "warnings"}`;

  return (
    <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", getComplianceStatusStyles(status))}>
      {label}
    </span>
  );
}

export function WorkflowCompliancePanel({
  compliance,
  quietHours,
  frequencyCap,
  compact = false,
}: {
  compliance?: WorkflowComplianceSummary | null;
  quietHours?: WorkflowQuietHours | null;
  frequencyCap?: WorkflowFrequencyCap | null;
  compact?: boolean;
}) {
  if (!compliance?.checks?.length) {
    return null;
  }

  return (
    <div
      className={cn(
        "space-y-3 rounded-lg border border-border bg-muted/30 p-3",
        compact && "space-y-2 border-0 bg-transparent p-0",
      )}
      data-testid="workflow-compliance-panel"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Compliance Gate
          </p>
          {!compact ? (
            <p className="text-xs text-muted-foreground">
              Review messaging enforcement before you publish or activate this workflow.
            </p>
          ) : null}
        </div>
        <WorkflowComplianceBadge compliance={compliance} />
      </div>

      <div className="flex flex-wrap gap-2">
        {compliance.checks.map((check) => (
          <span
            key={check.key}
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px]",
              check.state === "pass"
                ? "border-[var(--color-success)]/30 bg-[var(--color-success)]/5 text-[var(--color-success)]"
                : "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
            )}
            title={check.message}
          >
            {check.state === "pass" ? <ShieldCheck className="h-3 w-3" /> : <TriangleAlert className="h-3 w-3" />}
            <span>{check.label}</span>
          </span>
        ))}
      </div>

      {!compact ? (
        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-md border border-border bg-background/80 p-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Quiet Hours
            </p>
            <p className="mt-1 text-sm text-foreground">{formatQuietHours(quietHours)}</p>
          </div>
          <div className="rounded-md border border-border bg-background/80 p-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Frequency Cap
            </p>
            <p className="mt-1 text-sm text-foreground">{formatFrequencyCap(frequencyCap)}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
